import type { FormBuilderField } from "../../types/formBuilder";
import type {
  FormSubmissionDto,
  SubmissionsExportFieldColumn,
} from "../../types/submission";
import { isDisplayFieldType } from "../formBuilder/apiFieldTypes";
import { buildSubmissionAnswersMap } from "./normalizeSubmission";

export type ShareSubmissionColumn = {
  id: number;
  label: string;
  field: FormBuilderField;
};

function emptyShareField(
  id: number,
  label: string,
  name: string,
  order: number,
  overrides: Partial<FormBuilderField> = {},
): FormBuilderField {
  return {
    clientId: `share-field-${id}`,
    id,
    label,
    name,
    type: "text",
    required: false,
    order,
    choicesText: "",
    quizCorrect: [],
    quizPoints: 1,
    filterable: false,
    withTime: false,
    numberMin: null,
    numberMax: null,
    numberStep: null,
    numberInteger: false,
    minAge: null,
    maxAge: null,
    cityIds: [],
    cityLabels: {},
    choicesByField: "",
    choicesByMapText: {},
    choicesUrl: "",
    choicesPath: "",
    choicesItem: "",
    choicesValue: "",
    choicesByUrl: "",
    choicesByPath: "",
    choicesByItem: "",
    choicesByValue: "",
    patronymicRequired: false,
    presentation: "stacked",
    visibilityEnabled: false,
    visibilityField: "",
    visibilityOperator: "equals",
    visibilityValue: "",
    visibilityAction: "show",
    nameManuallyEdited: true,
    displayContent: "",
    displayImageUrl: "",
    displayImageNodeId: null,
    ...overrides,
    description: overrides.description ?? "",
  };
}

export function exportFieldColumnToBuilderField(
  column: SubmissionsExportFieldColumn,
  index: number,
): FormBuilderField {
  const cityLabels = column.cityLabels ?? {};
  const cityIds = Object.keys(cityLabels)
    .map((key) => Number(key))
    .filter((id) => Number.isFinite(id))
    .map((id) => Math.trunc(id));

  return emptyShareField(
    column.id,
    column.label,
    column.name ?? `field_${column.id}`,
    column.order ?? index,
    {
      type: column.type ?? "text",
      cityIds,
      cityLabels,
    },
  );
}

export function buildShareSubmissionColumns(
  exportFields: SubmissionsExportFieldColumn[],
  submissions: FormSubmissionDto[],
): ShareSubmissionColumn[] {
  const answerFields = exportFields.filter(
    (field) => !isDisplayFieldType(field.type),
  );
  const knownIds = new Set(exportFields.map((field) => field.id));
  const columns: ShareSubmissionColumn[] = answerFields.map((field, index) => ({
    id: field.id,
    label: field.label.trim() || field.name?.trim() || `Поле #${field.id}`,
    field: exportFieldColumnToBuilderField(field, index),
  }));

  const orphanIds = new Set<number>();
  for (const submission of submissions) {
    for (const fieldId of buildSubmissionAnswersMap(submission.data).keys()) {
      if (!knownIds.has(fieldId)) orphanIds.add(fieldId);
    }
  }

  let order = columns.length;
  for (const id of [...orphanIds].sort((a, b) => a - b)) {
    const label = `Поле #${id}`;
    columns.push({
      id,
      label,
      field: emptyShareField(id, label, `field_${id}`, order),
    });
    order += 1;
  }

  return columns;
}
